import _ from 'lodash'
import { BaseBalloonExtension } from './baseBalloonExtension'
import { CANVAS_ENTITY_EVENT, SHAPES } from '../../const'
import { EventManager } from '../../eventManager'
import type {
  ICircle,
  IGroup,
  ILine,
  INode,
  ISelectionHandles,
  IShape,
} from '../../backend/IRenderer'
import { isEllipseNode, isRectNode } from '../../backend/nodeGuards'
import {
  calculateDirectionPath,
  updateDirectionElements,
  initializeDirectionDragState,
  updateDirectionBalloonPosition,
  snapToDirections,
} from '../types/direction/stateManager'
import type { Vector2d } from '../../types'
import type { CanvasEngineEntity } from '../index'
import { TripodSelectionMixin } from '../types/tripod/tripodSelectionMixin'
import type { SelectionStrategy } from './selectionStrategy'

// 액션버튼그룹 위치 관련 상수
const BUTTON_POSITION_CONFIG = {
  TRANSFORMER_OFFSET_BELOW: 45, // transformer 아래쪽 경계에서의 거리
  TRANSFORMER_OFFSET_ABOVE: 75, // transformer 위쪽 경계에서의 거리
  CENTER_TOLERANCE: 50, // balloon 중앙 근처 허용 범위
  BELOW_THRESHOLD: 20, // shape 아래쪽 판단 기준
  SCREEN_MARGIN: 10, // 화면 경계에서의 여백
} as const

// 위치 동기화 이벤트 네임스페이스 일련번호(엔티티마다 따로 구독·해제 — 공용 '.buttonGroupElement'는 stage.off가 통째 지운다)
let positionSyncSeq = 0

export class BaseSelectionHandler extends TripodSelectionMixin(BaseBalloonExtension) {
  public TRANSFORMER_HIT_STROKE_WIDTH = 20

  // EventManager 인스턴스
  protected eventManager = EventManager.getInstance()

  // SelectionStrategy (타입별 선택 핸들러 로직)
  protected selectionStrategy?: SelectionStrategy

  // 버튼그룹 관련 프로퍼티 (외부 DOM 지원)
  protected __buttonGroupElement: HTMLElement | undefined
  protected __outsideClickHandler: ((event: MouseEvent) => void) | undefined
  protected __isButtonGroupHovered: boolean = false
  protected __buttonGroupResizeObserver: ResizeObserver | undefined

  protected __isTransformInProgress = false
  protected __isBalloonDragging = false
  protected __animationFrameId: number | undefined
  // 이벤트 기반 위치 동기화: 예약된 확인 rAF와 구독 네임스페이스
  protected __positionSyncRafId: number | undefined
  protected __positionSyncNs: string | undefined
  // 동기화 (재)시작 직후 첫 확인은 stage만 바뀌었어도 transformer를 갱신
  protected __positionSyncForceTransformer = false

  // 방향 재설정 모드 상태
  protected __isDirectionRedrawMode = false
  protected __redrawStartDirection: (Vector2d & { index: number }) | null = null

  // 위치 캐싱 관련 프로퍼티
  protected __lastPosition: { x: number; y: number } | undefined
  protected __lastScale: number = 1
  protected __lastStagePosition: { x: number; y: number } = { x: 0, y: 0 }

  /**
   * SelectionStrategy 설정
   * @param strategy - 타입별 선택 핸들러 전략
   */
  setSelectionStrategy(strategy: SelectionStrategy): void {
    this.selectionStrategy = strategy
  }

  /**
   * SelectionStrategy에서 사용할 수 있는 public API
   * 내부 protected/private 멤버에 안전하게 접근할 수 있도록 제공
   */

  // Shape 관련 getter
  public getShape(): IShape | undefined {
    return this.__shape
  }

  public getLine(): ILine | undefined {
    return this.__line
  }

  public getBrush(): IGroup | undefined {
    return this.__brush
  }

  public getShapeTransformer(): ISelectionHandles | undefined {
    return this.__shapeTransformer
  }

  public setShapeTransformer(transformer: ISelectionHandles | undefined): void {
    this.__shapeTransformer = transformer
  }

  // Transformer 관련 public 메서드
  public destroyTransformer(): void {
    this.__destroyTransformer()
  }

  public syncBalloonToLinePublic(): void {
    this.__SyncBalloonToLine()
  }

  public linePositionCalibration(): void {
    this.__LinePositionCalibration()
  }

  // 애니메이션 관련 public 메서드
  public removeShapeAnimation(): void {
    this.rippleManager.removeShapeAnimation()
  }

  public addShapeAnimation(): void {
    this.rippleManager.addShapeAnimation()
  }

  public updateRipplePosition(): void {
    this.rippleManager.updateRipplePosition()
  }

  // 버튼 관련 public 메서드
  public hideButtonGroup(): void {
    this.__hideButtonGroup()
  }

  public showButtonGroup(): void {
    this.__showButtonGroup()
  }

  public updateButtonPosition(): void {
    this.__updateButtonPosition()
  }

  // 색상 getter
  public getEntityColor(): string {
    return this.__color
  }

  // EventManager getter
  public getEventManager(): EventManager {
    return this.eventManager
  }

  // Balloon 위치 업데이트 public 메서드
  public updateBalloonPositionPublic(): void {
    this.updateBalloonPosition()
  }

  /**
   * 도형을 프로그래밍적으로 선택하는 함수 (템플릿 메서드 패턴)
   * @returns
   */
  select(): BaseSelectionHandler {
    super.select()
    // 선택했을때만 드래그 가능
    this.__shape?.draggable(true)

    // 타입별 핸들러 추가 (템플릿 메서드)
    this.addTypeSpecificHandlers()

    // 버튼 그룹
    this.__addButton()

    // 선택 시각 요소 추가 완료 후 명시적 렌더
    this.renderer.layers.system.batchDraw()

    return this
  }

  /**
   * 타입별 핸들러 추가 (템플릿 메서드)
   * SelectionStrategy를 통해 타입별 핸들러를 위임합니다.
   */
  protected addTypeSpecificHandlers(): void {
    if (this.selectionStrategy) {
      this.selectionStrategy.addHandlers(this)
    }
  }

  /**
   * 도형을 프로그래밍적으로 선택 해제하는 함수
   * @returns
   */
  blur(): void {
    super.blur()
    this.__destroyTransformer()
    this.__stopPositionSync() // 실시간 동기화 정지
    this.__removeButton()
    this.__shape?.draggable(false)

    // TRIPOD 관련 stage 이벤트 정리
    const tripodMixin1 = this as unknown as { __cleanupTripodStageEvents?: () => void }
    if (tripodMixin1.__cleanupTripodStageEvents) {
      tripodMixin1.__cleanupTripodStageEvents()
    }

    // TRIPOD 편집 버튼들 제거
    const tripodMixin2 = this as unknown as { __removeTripodEditButtons?: () => void }
    if (tripodMixin2.__removeTripodEditButtons) {
      tripodMixin2.__removeTripodEditButtons()
    }

    // TRIPOD 개별 요소 이벤트들 제거 (startPoint, 주축선) - 선택 해제 시에만
    if (this.entityType === SHAPES.TRIPOD) {
      const tripodMixin3 = this as unknown as { __removeTripodElementHandlers?: () => void }
      if (tripodMixin3.__removeTripodElementHandlers) {
        tripodMixin3.__removeTripodElementHandlers()
      }
    }

    // TRIPOD startpoint hover 상태 리셋
    const tripodMixin4 = this as unknown as { __resetTripodStartPointHover?: () => void }
    if (tripodMixin4.__resetTripodStartPointHover) {
      tripodMixin4.__resetTripodStartPointHover()
    }
    this.canvasEngine.restoreAllEntitiyListening()
    // 위치 캐시 초기화
    this.__lastPosition = undefined
    this.__lastScale = 1
    this.__lastStagePosition = { x: 0, y: 0 }
    // balloon 드래그 상태 초기화
    this.__isBalloonDragging = false
    // 방향 재설정 모드 상태 초기화
    this.__isDirectionRedrawMode = false
    this.__redrawStartDirection = null
    // buttonGroup 호버 상태 초기화
    this.__isButtonGroupHovered = false
    // 캐시 초기화
    this.renderer.hints.unmarkStatic(this.node)
    // 브러시는 캐시 제외 (엔진 캐시 정책과 동일 — 대형 비트맵이 저사양 렌더를 느리게 함)
    if (this.isBrush()) return
    // 해상도(화면 배율 연동)·hit 배율·여백은 엔진 캐시 정책으로
    this.canvasEngine.markEntityStatic(this as unknown as CanvasEngineEntity)
  }

  /**
   * balloon 위치 업데이트 시 복사버튼 위치도 함께 업데이트
   */
  protected updateBalloonPosition(): void {
    super.updateBalloonPosition()
    // balloon 위치가 변경되면 복사버튼 위치도 업데이트
    this.__updateButtonPosition()
  }

  setBalloonTag(tag: { top: string; bottom: string }): void {
    super.setBalloonTag(tag)
  }
  hideBalloonTag(): void {
    super.hideBalloonTag()
  }

  changeColor(color: string): void {
    super.changeColor(color)
    if (this.__lineTransformerEndHandle) {
      this.__lineTransformerEndHandle.stroke(color)
    }
    if (this.__lineTransformerStartHandle) {
      this.__lineTransformerStartHandle.stroke(color)
    }

    // TRIPOD인 경우 startpoint들과 connectionline들도 색상 업데이트
    if (this.isTripod()) {
      const tripodMixin = this as unknown as { __updateTripodColors: (color: string) => void }
      tripodMixin.__updateTripodColors(color)
    }
  }

  changeBalloonFillColor(color: string): void {
    super.changeBalloonFillColor(color)
    if (this.__lineTransformerEndHandle) {
      this.__lineTransformerEndHandle.stroke(color)
    }
    if (this.__lineTransformerStartHandle) {
      this.__lineTransformerStartHandle.stroke(color)
    }
  }
  changeBalloonFontColor(color: string): void {
    super.changeBalloonFontColor(color)
    if (this.__lineTransformerEndHandle) {
      this.__lineTransformerEndHandle.stroke(color)
    }
    if (this.__lineTransformerStartHandle) {
      this.__lineTransformerStartHandle.stroke(color)
    }
  }

  protected __SyncBalloonToLine(): void {
    if (!this.__line) return
    const points = this.getPoints()
    const x = this.__line.x()
    const y = this.__line.y()
    this.syncBalloonToLine([points[0] + x, points[1] + y, points[2] + x, points[3] + y])
  }

  protected __LinePositionCalibration(): void {
    if (!this.__line) return
    if (!this.__lineTransformerStartHandle) return
    if (!this.__lineTransformerEndHandle) return

    const _points = this.getPoints()

    this.setPoints([
      _points[0] + this.__line.x(),
      _points[1] + this.__line.y(),
      _points[2] + this.__line.x(),
      _points[3] + this.__line.y(),
    ])

    this.setLinePosition(0, 0)

    const changedPoints = this.getPoints()

    this.__lineTransformerStartHandle.x(changedPoints[0])
    this.__lineTransformerStartHandle.y(changedPoints[1])
    this.__lineTransformerEndHandle.x(changedPoints[2])
    this.__lineTransformerEndHandle.y(changedPoints[3])
  }

  protected __destroyTransformer(): void {
    if (this.__lineTransformerStartHandle) {
      this.__lineTransformerStartHandle.destroy()
    }
    if (this.__lineTransformerEndHandle) {
      this.__lineTransformerEndHandle.destroy()
    }
    if (this.__shapeTransformer) {
      this.__shapeTransformer.destroy()
    }
  }

  /**
   * 외부 버튼그룹 컴포넌트를 추가하는 함수
   */
  protected __addButton(): void {
    this.__removeButton() // 기존 버튼 제거

    // 버튼그룹을 위한 임시 컨테이너 생성
    const container = document.createElement('div')
    container.id = `action-button-container-${this.getId()}`
    container.style.position = 'fixed'
    container.style.zIndex = '1000'
    container.style.pointerEvents = 'auto'

    // body에 추가 (Canvas 컨테이너 설정 문제 회피)
    document.body.appendChild(container)

    // 외부 버튼그룹 컴포넌트 생성 및 설정 요청
    this.canvasEngine.events.onShowButton(container, this.getId())

    // stage 변환 이벤트 리스너 추가
    this.__setupStageScaleEvents()

    // balloon 드래그 이벤트 처리
    this.__setupBalloonDragEvents()
  }

  /**
   * 외부 버튼그룹 엘리먼트를 설정하는 함수
   * @param element 버튼그룹 HTML 엘리먼트
   * @returns 설정 성공 여부
   */
  public setButtonElement(element: HTMLElement | null): boolean {
    if (!element) {
      return false
    }

    // 엘리먼트 설정
    this.__buttonGroupElement = element
    // 합성 레이어로 승격 — 팬 중 위치만 바뀌어도 페이지 전체를 다시 래스터하지 않게(A21s 팬 rAF +7ms였다)
    element.style.willChange = 'transform'

    // ActionButtonGroup 호버 이벤트 설정
    this.__setupButtonGroupHoverEvents()

    // 초기 위치 설정 및 캐시 초기화
    this.__initializePositionCache()
    this.__updateButtonPosition()

    // 컴포넌트 크기 변화 시 위치 재계산 (최소화/펼치기 등)
    if (typeof ResizeObserver !== 'undefined') {
      this.__buttonGroupResizeObserver?.disconnect()
      this.__buttonGroupResizeObserver = new ResizeObserver(() => {
        if (this.__buttonGroupElement && this.isSelected) {
          this.__updateButtonPosition()
        }
      })
      this.__buttonGroupResizeObserver.observe(element)
    }

    // 실시간 동기화 시작 (anchor 효과)
    this.__startPositionSync()

    return true
  }

  /**
   * 외부 컴포넌트에서 호출할 수 있는 복사 버튼 클릭 핸들러
   */
  public handleButtonClick(): void {
    this.__handleCopyButtonClick()
  }

  /**
   * 외부 컴포넌트에서 호출할 수 있는 삭제 버튼 클릭 핸들러
   */
  public handleDeleteButtonClick(): void {
    this.__handleDeleteButtonClick()
  }

  /**
   * ActionButtonGroup 호버 이벤트 설정
   */
  protected __setupButtonGroupHoverEvents(): void {
    if (!this.__buttonGroupElement) return

    this.__buttonGroupElement.addEventListener('mouseenter', () => {
      this.__isButtonGroupHovered = true
    })

    this.__buttonGroupElement.addEventListener('mouseleave', () => {
      this.__isButtonGroupHovered = false
    })

    // 터치 이벤트도 지원
    this.__buttonGroupElement.addEventListener('touchstart', () => {
      this.__isButtonGroupHovered = true
    })

    this.__buttonGroupElement.addEventListener('touchend', () => {
      this.__isButtonGroupHovered = false
    })
  }

  /**
   * balloon 드래그 이벤트 설정
   */
  protected __setupBalloonDragEvents(): void {
    if (!this.balloon) return

    this.balloon.head.rect.off(CANVAS_ENTITY_EVENT.BALLOON_DRAG_START)
    this.balloon.head.rect.on(CANVAS_ENTITY_EVENT.BALLOON_DRAG_START, () => {
      this.__isBalloonDragging = true
      // balloon 드래그 모드 활성화 (bend point 스냅 비활성화)
      this.setBalloonDragMode(true)
    })

    // 기존 selection용 dragmove 이벤트만 제거
    this.balloon.head.rect.off('dragmove.selection')
    this.balloon.head.rect.off(CANVAS_ENTITY_EVENT.BALLOON_DRAG_END)

    // balloon 드래그 중 버튼 위치 업데이트
    this.balloon.head.rect.on('dragmove.selection', () => {
      this.__updateButtonPosition()

      // 방향도형의 경우 드래그 방향에 따라 경로 업데이트
      if (
        this.entityType === SHAPES.DIRECTION &&
        this.__directionShape &&
        this.__directionPoints.length >= 4
      ) {
        const balloonPos = this.balloon!.getPosition()
        const dragPos = {
          x: balloonPos.x,
          y: balloonPos.y,
        }

        // 경로 업데이트
        this.__updateDirectionPath(dragPos)

        // 벌룬을 항상 끝점에 고정
        if (this.__directionPoints.length >= 4) {
          const endX = this.__directionPoints[this.__directionPoints.length - 2]
          const endY = this.__directionPoints[this.__directionPoints.length - 1]
          this.balloon!.setPosition({ x: endX, y: endY })
          this.balloon!.head.updateTextPosition()
        }
      } else {
        // 일반 도형의 경우 벌룬 위치 실시간 업데이트
        this.updateBalloonPosition()
      }
    })

    // balloon 드래그 종료 시 최종 위치 업데이트
    this.balloon.head.rect.on(CANVAS_ENTITY_EVENT.BALLOON_DRAG_END, () => {
      this.__isBalloonDragging = false
      // balloon 드래그 모드 비활성화 (bend point 스냅 재활성화)
      this.setBalloonDragMode(false)
      this.__updateButtonPosition()

      // 방향도형의 경우 최종 업데이트 및 상태 리셋
      if (this.entityType === SHAPES.DIRECTION) {
        // 방향 재설정 모드 상태 초기화
        this.__isDirectionRedrawMode = false
        this.__redrawStartDirection = null
        // 벌룬 위치를 끝점으로 보정
        if (this.__directionPoints.length >= 4) {
          const endX = this.__directionPoints[this.__directionPoints.length - 2]
          const endY = this.__directionPoints[this.__directionPoints.length - 1]
          this.balloon!.setPosition({ x: endX, y: endY })
          this.balloon!.head.updateTextPosition()

          // balloonDragDiff 재계산 (상대좌표로)
          if (this.balloonDragDiff) {
            const finalBalloonPos = this.balloon!.getPosition()
            this.balloonDragDiff.x = finalBalloonPos.x
            this.balloonDragDiff.y = finalBalloonPos.y
          }
        }

        // 최종 시각 업데이트
        this.__updateDirectionRender()
        this.node.getLayer()?.batchDraw()

        // 드래그 상태 리셋
        this.__directionStateManager.reset()
      }
    })
  }

  /**
   * Stage scale 변경 이벤트 설정
   */
  protected __setupStageScaleEvents(): void {
    const stage = this.renderer.stage
    if (!stage) return

    // 기존 이벤트 제거 (중복 방지)
    stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_SCALEX_CHANGE)
    stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_SCALEY_CHANGE)
    stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_DRAG_MOVE)
    stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_WHEEL)
    stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_DRAG_END)

    // 팬 끝: 위치 동기화는 1px 넘게 바뀌어야 버튼을 옮긴다(위 dragmove 리스너는 동기화 중 건너뜀).
    // 마지막 동기화 뒤 남은 1px 미만 잔차를 끝에서 한 번 맞춘다
    stage.on(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_DRAG_END, () => {
      if (this.__buttonGroupElement && this.isSelected) this.__updateButtonPosition()
    })

    // stage의 변환 이벤트 감지 (zoom, pan)
    stage.on(
      `${CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_SCALEX_CHANGE} ${CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_SCALEY_CHANGE} ${CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_DRAG_MOVE} ${CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_WHEEL}`,
      () => {
        // 위치 동기화가 도는 중이면 둘 다 중복: transformer는 Konva가 absoluteTransformChange로 이미 갱신했고,
        // 버튼그룹은 동기화가 같은 프레임 rAF에서 맞춘다(이쪽 setTimeout은 그 뒤). 동기화가 멈춘 동안(변형 중 숨김)은 기존대로
        if (this.__positionSyncNs !== undefined) return

        // transformer 업데이트 (buttonGroup 호버 중이 아닐 때만)
        if (this.__shapeTransformer && this.isSelected && !this.__isButtonGroupHovered) {
          // transformer의 nodes가 유효한지 확인
          const nodes = this.__shapeTransformer.nodes()
          const hasValidNodes =
            nodes &&
            nodes.length > 0 &&
            nodes.every((node: INode) => {
              // destroy된 노드는 parent가 null이 됨
              try {
                return node && node.getParent() !== null
              } catch {
                return false
              }
            })

          if (hasValidNodes) {
            this.__shapeTransformer.forceUpdate()
            this.__shapeTransformer.node.getLayer()?.batchDraw()
          }
        }

        // transformer 처리 완료 후 buttonGroup 위치 업데이트
        if (this.__buttonGroupElement) {
          // 다음 프레임에서 buttonGroup 업데이트하여 transformer 안정화 후 처리
          setTimeout(() => {
            if (this.__buttonGroupElement && this.isSelected) {
              this.__updateButtonPosition()
            }
          }, 0)
        }
      },
    )
  }

  /**
   * 실시간 위치 동기화 시작 (anchor 효과)
   */
  protected __startPositionSync(): void {
    this.__stopPositionSync() // 기존 동기화 정리
    // 멈춰 있던 동안(변형 중 등 Konva가 갱신을 건너뛸 수 있는 때) stage가 바뀌었을 수 있다 → 첫 확인은 갱신
    this.__positionSyncForceTransformer = true
    this.__startPositionSyncByEvents()
  }

  /**
   * 이벤트 기반 위치 동기화. stage x·y·scaleX, 엔티티 node x·y의 변경 이벤트가 오면
   * 다음 프레임에 한 번 확인한다(여러 이벤트는 rAF 1회로 합침). 변화가 없으면 프레임을 깨우지 않는다 — 매 프레임
   * rAF 폴링은 그리기가 0이어도 vsync를 계속 깨워 A21s에서 앱 16%+렌더러 26%를 썼다.
   * Konva는 값이 바뀌는 setter마다 xChange 등을 쏘므로(_setAttr) 폴링과 같은 시점에 같은 갱신을 한다
   */
  protected __startPositionSyncByEvents(): void {
    const stage = this.renderer.stage
    const ns = `positionSync${++positionSyncSeq}`
    this.__positionSyncNs = ns

    const schedule = (): void => {
      if (this.__positionSyncRafId !== undefined) return
      this.__positionSyncRafId = requestAnimationFrame(() => {
        this.__positionSyncRafId = undefined
        if (!this.__buttonGroupElement || !this.isSelected) {
          // 폴링 루프가 스스로 멈추던 조건과 같다
          this.__stopPositionSync()
          return
        }
        this.__syncPositionIfChanged()
      })
    }

    stage.on(`xChange.${ns} yChange.${ns} scaleXChange.${ns}`, schedule)
    this.node.on(`xChange.${ns} yChange.${ns}`, schedule)
    // 시작(숨김 뒤 재시작 포함) 직후 한 번 확인 — 폴링의 첫 프레임처럼 멈춰 있던 동안의 변화를 따라잡는다
    schedule()
  }

  /**
   * stage·엔티티 위치가 바뀌었으면 transformer와 버튼그룹 위치를 갱신
   */
  protected __syncPositionIfChanged(): void {
    const prevEntityPos = this.__lastPosition
    // stage position, scale이 변경되었을 때만 업데이트
    if (!this.__hasPositionChanged()) return

    // stage만 바뀌었으면 transformer는 Konva가 노드의 absoluteTransformChange로 이미 갱신했다(중복 forceUpdate 생략).
    // Konva는 변형·드래그 중엔 그 갱신을 건너뛰므로 그때는 기존대로
    const force = this.__positionSyncForceTransformer
    this.__positionSyncForceTransformer = false
    const stageOnly =
      !force &&
      !this.__isTransformInProgress &&
      !this.__shapeTransformer?.node.isDragging() &&
      prevEntityPos !== undefined &&
      prevEntityPos.x === this.node.x() &&
      prevEntityPos.y === this.node.y()

    // transformer 먼저 동기화 (buttonGroup 호버 중이 아닐 때만)
    if (!stageOnly && this.__shapeTransformer && this.isSelected && !this.__isButtonGroupHovered) {
      // transformer의 nodes가 유효한지 확인
      const nodes = this.__shapeTransformer.nodes()
      const hasValidNodes =
        nodes &&
        nodes.length > 0 &&
        nodes.every((node: INode) => {
          // destroy된 노드는 parent가 null이 됨
          try {
            return node && node.getParent() !== null
          } catch {
            return false
          }
        })

      if (hasValidNodes) {
        this.__shapeTransformer.forceUpdate()
        this.__shapeTransformer.node.getLayer()?.batchDraw()
      }
    }

    // 2. transformer 완료 후 buttonGroup 업데이트
    this.__updateButtonPosition()
  }

  /**
   * 위치 캐시 초기화
   */
  protected __initializePositionCache(): void {
    const stage = this.renderer.stage
    this.__lastScale = stage.scaleX()
    this.__lastStagePosition = { ...stage.position() }
    this.__lastPosition = { x: this.node.x(), y: this.node.y() }
  }

  /**
   * stage position이나 scale이 변경되었는지 확인
   */
  protected __hasPositionChanged(): boolean {
    const stage = this.renderer.stage
    const currentScale = stage.scaleX()
    const currentStagePos = stage.position()

    // entity 자체 위치도 확인
    const currentEntityPos = { x: this.node.x(), y: this.node.y() }

    const scaleChanged = Math.abs(this.__lastScale - currentScale) > 0.001
    const stagePositionChanged =
      Math.abs(this.__lastStagePosition.x - currentStagePos.x) > 1 ||
      Math.abs(this.__lastStagePosition.y - currentStagePos.y) > 1
    const entityPositionChanged =
      !this.__lastPosition ||
      Math.abs(this.__lastPosition.x - currentEntityPos.x) > 1 ||
      Math.abs(this.__lastPosition.y - currentEntityPos.y) > 1

    if (scaleChanged || stagePositionChanged || entityPositionChanged) {
      // 변경된 값들 저장
      this.__lastScale = currentScale
      this.__lastStagePosition = { ...currentStagePos }
      this.__lastPosition = { ...currentEntityPos }
      return true
    }

    return false
  }

  /**
   * 실시간 위치 동기화 정지
   */
  protected __stopPositionSync(): void {
    if (this.__animationFrameId) {
      cancelAnimationFrame(this.__animationFrameId)
      this.__animationFrameId = undefined
    }
    if (this.__positionSyncRafId !== undefined) {
      cancelAnimationFrame(this.__positionSyncRafId)
      this.__positionSyncRafId = undefined
    }
    if (this.__positionSyncNs) {
      const ns = this.__positionSyncNs
      this.__positionSyncNs = undefined
      this.renderer.stage?.off(`xChange.${ns} yChange.${ns} scaleXChange.${ns}`)
      this.node?.off(`xChange.${ns} yChange.${ns}`)
    }
  }

  /**
   * stage 클릭 이벤트 처리 함수
   */
  // 메뉴 숨김 로직은 selectShape blur 시점에서 처리됨

  /**
   * 외부 버튼그룹 엘리먼트의 위치를 업데이트하는 함수
   */
  protected __updateExternalButtonPosition(): void {
    if (!this.__buttonGroupElement) return

    const stage = this.renderer.stage
    const scale = stage.scaleX()
    const stagePos = stage.position()
    const containerRect = stage.container().getBoundingClientRect()

    let canvasButtonCenterX: number
    let canvasButtonCenterY: number

    // TRIPOD의 경우 특별한 위치 계산 로직 적용
    if (this.isTripod()) {
      const tripodMixin = this as unknown as {
        __calculateTripodButtonPosition?: () => { x: number; y: number }
      }
      if (tripodMixin.__calculateTripodButtonPosition) {
        const tripodButtonPosition = tripodMixin.__calculateTripodButtonPosition()
        canvasButtonCenterX = tripodButtonPosition.x
        canvasButtonCenterY = tripodButtonPosition.y
      } else {
        // fallback: 기본 위치
        const belowPosition = this.__calculateTransformerBasedPosition(false)
        canvasButtonCenterX = belowPosition.x
        canvasButtonCenterY = belowPosition.y
      }
    } else if (this.balloon) {
      // balloon이 있는 경우 balloon 위치 확인하여 버튼 위치 결정
      const balloonPos = this.balloon.getPosition()
      const balloonY = balloonPos.y + this.node.y()

      // transformer 경계 정보 가져오기
      const transformerBounds = this.__getTransformerBounds()

      // balloon과 shape transformer 중심의 상대적 위치 계산
      const deltaY = balloonY - transformerBounds.centerY

      // balloon이 shape 아래쪽에 있는지 확인
      const isBelowShape = deltaY > BUTTON_POSITION_CONFIG.BELOW_THRESHOLD

      if (isBelowShape) {
        // balloon이 shape 아래쪽에 있을 때 → shape 위쪽에 버튼 표시
        const abovePosition = this.__calculateTransformerBasedPosition(true)
        canvasButtonCenterX = abovePosition.x
        canvasButtonCenterY = abovePosition.y
      } else {
        // 기본 위치: shape 하단 경계 아래쪽
        const belowPosition = this.__calculateTransformerBasedPosition(false)
        canvasButtonCenterX = belowPosition.x
        canvasButtonCenterY = belowPosition.y
      }
    } else {
      // balloon이 없는 경우 기본 위치 (아래쪽)
      const belowPosition = this.__calculateTransformerBasedPosition(false)
      canvasButtonCenterX = belowPosition.x
      canvasButtonCenterY = belowPosition.y
    }

    // Canvas 좌표를 DOM 좌표로 변환 (anchor처럼 동작)
    const domCenterX = containerRect.left + (canvasButtonCenterX * scale + stagePos.x)
    const domCenterY = containerRect.top + (canvasButtonCenterY * scale + stagePos.y)

    // 실제 엘리먼트 크기 가져오기 (고정크기로 사용)
    const elementWidth = this.__buttonGroupElement.offsetWidth || 100 // 기본값 설정
    const elementHeight = this.__buttonGroupElement.offsetHeight || 80 // 기본값 설정

    // 중심점을 기준으로 왼쪽 상단 좌표 계산
    const finalX = domCenterX - elementWidth / 2
    const finalY = domCenterY - elementHeight / 2

    // 화면 경계 체크 및 조정
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    let adjustedX = finalX
    let adjustedY = finalY

    // 화면 경계를 벗어나지 않도록 조정
    if (adjustedX < BUTTON_POSITION_CONFIG.SCREEN_MARGIN) {
      adjustedX = BUTTON_POSITION_CONFIG.SCREEN_MARGIN
    }
    if (adjustedY < BUTTON_POSITION_CONFIG.SCREEN_MARGIN) {
      adjustedY = BUTTON_POSITION_CONFIG.SCREEN_MARGIN
    }
    if (adjustedX + elementWidth > windowWidth - BUTTON_POSITION_CONFIG.SCREEN_MARGIN) {
      adjustedX = windowWidth - elementWidth - BUTTON_POSITION_CONFIG.SCREEN_MARGIN
    }
    if (adjustedY + elementHeight > windowHeight - BUTTON_POSITION_CONFIG.SCREEN_MARGIN) {
      adjustedY = windowHeight - elementHeight - BUTTON_POSITION_CONFIG.SCREEN_MARGIN
    }

    // 요소 위치 설정 (고정크기, anchor처럼 동작)
    this.__buttonGroupElement.style.position = 'fixed'
    this.__buttonGroupElement.style.left = `${adjustedX}px`
    this.__buttonGroupElement.style.top = `${adjustedY}px`
    this.__buttonGroupElement.style.transform = 'none'
    this.__buttonGroupElement.style.transformOrigin = 'top left'
    this.__buttonGroupElement.style.zIndex = '1000'
    this.__buttonGroupElement.style.pointerEvents = 'auto'
  }

  /**
   * 복사버튼 클릭 핸들러 - canvas.ts의 handleCopyShape로 위임
   */
  protected __handleCopyButtonClick(): void {
    try {
      this.canvasEngine.events.onCopyShape(this.getId())
    } catch (error) {
      console.error('❌ 복사 이벤트 전달 중 오류:', error)
    }
  }

  /**
   * 삭제버튼 클릭 핸들러 - canvas.ts의 handleDeleteShape로 위임
   */
  protected __handleDeleteButtonClick(): void {
    try {
      this.canvasEngine.events.onDeleteShapes([this.getId()])
    } catch (error) {
      console.error('❌ 삭제 이벤트 전달 중 오류:', error)
    }
  }

  /**
   * 외부 버튼그룹 엘리먼트를 제거하는 함수
   */
  protected __removeButton(): void {
    // 실시간 동기화 정지
    this.__stopPositionSync()

    // ResizeObserver 정리
    this.__buttonGroupResizeObserver?.disconnect()
    this.__buttonGroupResizeObserver = undefined

    // stage 변환 이벤트 정리
    const stage = this.renderer.stage
    if (stage) {
      stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_SCALEX_CHANGE)
      stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_SCALEY_CHANGE)
      stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_DRAG_MOVE)
      stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_WHEEL)
      stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_DRAG_END)
      stage.off(CANVAS_ENTITY_EVENT.BUTTON_GROUP_STAGE_CLICK)
    }

    // 버튼그룹 숨김 요청
    this.canvasEngine.events.onHideButton(this.getId())

    // 컨테이너 제거
    const container = document.getElementById(`action-button-container-${this.getId()}`)
    if (container) {
      container.remove()
    }

    if (this.__buttonGroupElement) {
      this.__buttonGroupElement.style.willChange = ''
      this.__buttonGroupElement = undefined
    }
  }

  /**
   * 외부 버튼그룹 엘리먼트를 숨기는 함수
   */
  protected __hideButtonGroup(): void {
    // transform 중에는 실시간 동기화 일시 정지
    this.__stopPositionSync()

    if (this.__buttonGroupElement) {
      this.__buttonGroupElement.style.visibility = 'hidden'
    }
  }

  /**
   * 외부 버튼그룹 엘리먼트를 보이게 하는 함수
   */
  protected __showButtonGroup(): void {
    if (this.__isTransformInProgress) return

    if (this.__buttonGroupElement) {
      this.__buttonGroupElement.style.visibility = 'visible'
      // 보이게 될 때 실시간 동기화 재시작 (anchor 효과)
      this.__startPositionSync()
    }
  }

  /**
   * 외부 버튼그룹 엘리먼트 위치를 업데이트하는 함수
   */

  /**
   * 외부 버튼그룹 엘리먼트 위치를 업데이트하는 함수
   */
  protected __updateButtonPosition(): void {
    if (!this.__buttonGroupElement) {
      return
    }

    if (this.__isTransformInProgress && !this.__isBalloonDragging) {
      this.__hideButtonGroup()
      return
    }

    // 위치 업데이트
    this.__updateExternalButtonPosition()
  }

  /**
   * shape(entity)의 중심점을 계산하는 함수
   */
  protected __getShapeCenter(): { x: number; y: number } {
    if (this.__line) {
      // Line의 경우 중점 계산
      const points = this.__line.points()
      const lineX = this.__line.x()
      const lineY = this.__line.y()
      return {
        x: (points[0] + points[2]) / 2 + lineX,
        y: (points[1] + points[3]) / 2 + lineY,
      }
    } else if (this.__shape) {
      // Shape의 경우 entity 위치 + shape 중심점
      const entityX = this.node.x()
      const entityY = this.node.y()

      if (isEllipseNode(this.__shape)) {
        return {
          x: entityX + this.__shape.x(),
          y: entityY + this.__shape.y(),
        }
      } else if (isRectNode(this.__shape)) {
        return {
          x: entityX + this.__shape.x() + this.__shape.width() / 2,
          y: entityY + this.__shape.y() + this.__shape.height() / 2,
        }
      } else {
        return {
          x: entityX + this.__shape.x(),
          y: entityY + this.__shape.y(),
        }
      }
    } else if (this.isBrush() && this.__brush) {
      // Brush의 경우 브러시 그룹 내 모든 Line들의 경계 상자 계산
      const allLines = this.__brush.find('Line') as ILine[]
      const visibleLines = allLines.filter(
        (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
      )

      if (visibleLines.length === 0) return { x: this.node.x(), y: this.node.y() }

      let minX = Number.MAX_VALUE
      let minY = Number.MAX_VALUE
      let maxX = Number.MIN_VALUE
      let maxY = Number.MIN_VALUE

      // 모든 가시 Line들의 점을 순회하며 경계 계산
      for (const line of visibleLines) {
        const points = line.points()
        for (let i = 0; i < points.length; i += 2) {
          const x = points[i]
          const y = points[i + 1]

          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }
      }

      // 경계 상자의 중심점 계산
      return {
        x: minX + (maxX - minX) / 2,
        y: minY + (maxY - minY) / 2,
      }
    }

    // 기본값: entity 자체의 위치
    return {
      x: this.node.x(),
      y: this.node.y(),
    }
  }

  /**
   * transformer의 경계 영역을 계산하는 함수
   */
  protected __getTransformerBounds(): {
    top: number
    bottom: number
    left: number
    right: number
    centerX: number
    centerY: number
  } {
    if (this.isLine()) {
      // Line의 경우 start/end handle 기준으로 경계 계산
      const points = this.getPoints()
      const lineX = this.__line?.x() || 0
      const lineY = this.__line?.y() || 0

      const x1 = points[0] + lineX
      const y1 = points[1] + lineY
      const x2 = points[2] + lineX
      const y2 = points[3] + lineY

      const minX = Math.min(x1, x2)
      const maxX = Math.max(x1, x2)
      const minY = Math.min(y1, y2)
      const maxY = Math.max(y1, y2)

      // handle 크기(radius: 5) + hit area 고려
      const handleRadius = 15

      return {
        left: minX - handleRadius,
        right: maxX + handleRadius,
        top: minY - handleRadius,
        bottom: maxY + handleRadius,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
      }
    } else if (this.isDirection()) {
      // 방향 도형의 경우, 핸들(centerPoint)을 기준으로 경계 계산
      if (!this.__shape) return { left: 0, right: 0, top: 0, bottom: 0, centerX: 0, centerY: 0 }

      const stage = this.renderer.stage
      const scale = stage.scaleX()
      const handlePos = this.__shape.position()
      const centerX = handlePos.x
      const centerY = handlePos.y

      const padding = 20
      const anchorSize = 20
      const handleRadius = (this.__shape as ICircle).radius()

      return {
        left: centerX - handleRadius - padding / scale,
        right: centerX + handleRadius + padding / scale,
        top: centerY - handleRadius - padding / scale - anchorSize / scale, // rotate handle 고려
        bottom: centerY + handleRadius + padding / scale,
        centerX: centerX,
        centerY: centerY,
      }
    } else if (this.__shape && this.__shapeTransformer) {
      // Shape의 경우 transformer 설정 기준으로 계산
      const stage = this.renderer.stage
      const scale = stage.scaleX()

      const entityX = this.node.x()
      const entityY = this.node.y()

      if (isEllipseNode(this.__shape)) {
        const radiusX = this.__shape.radiusX() * this.__shape.scaleX()
        const radiusY = this.__shape.radiusY() * this.__shape.scaleY()

        if (this.isPoint()) {
          // Point의 경우 특별 처리 (rotate 비활성화, anchor 숨김)
          const padding = 20
          return {
            left: entityX - radiusX - padding / scale,
            right: entityX + radiusX + padding / scale,
            top: entityY - radiusY - padding / scale,
            bottom: entityY + radiusY + padding / scale,
            centerX: entityX,
            centerY: entityY,
          }
        } else {
          // 일반 Ellipse의 경우
          const padding = 20
          const anchorSize = 20
          return {
            left: entityX - radiusX - padding / scale,
            right: entityX + radiusX + padding / scale,
            top: entityY - radiusY - padding / scale - anchorSize / scale, // rotate handle 고려
            bottom: entityY + radiusY + padding / scale,
            centerX: entityX,
            centerY: entityY,
          }
        }
      } else if (isRectNode(this.__shape)) {
        const width = this.__shape.width() * this.__shape.scaleX()
        const height = this.__shape.height() * this.__shape.scaleY()

        // Rect의 경우 (일반 shape)
        const padding = 20
        const anchorSize = 20
        return {
          left: entityX - padding / scale,
          right: entityX + width + padding / scale,
          top: entityY - padding / scale - anchorSize / scale, // rotate handle 고려
          bottom: entityY + height + padding / scale,
          centerX: entityX + width / 2,
          centerY: entityY + height / 2,
        }
      }
    } else if (this.isBrush() && this.__brush) {
      // Brush의 경우 브러시 그룹 내 모든 Line들의 경계 상자 계산
      const allLines = this.__brush.find('Line') as ILine[]
      const visibleLines = allLines.filter(
        (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
      )

      if (visibleLines.length === 0) {
        // 가시 Line이 없으면 기본값 반환
        return {
          left: this.node.x() - 20,
          right: this.node.x() + 20,
          top: this.node.y() - 20,
          bottom: this.node.y() + 20,
          centerX: this.node.x(),
          centerY: this.node.y(),
        }
      }

      let minX = Number.MAX_VALUE
      let minY = Number.MAX_VALUE
      let maxX = Number.MIN_VALUE
      let maxY = Number.MIN_VALUE

      // 모든 가시 Line들의 점을 순회하며 경계 계산
      for (const line of visibleLines) {
        const points = line.points()
        for (let i = 0; i < points.length; i += 2) {
          const x = points[i]
          const y = points[i + 1]

          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }
      }

      const stage = this.renderer.stage
      const scale = stage.scaleX()
      const padding = 20

      return {
        left: minX - padding / scale,
        right: maxX + padding / scale,
        top: minY - padding / scale,
        bottom: maxY + padding / scale,
        centerX: minX + (maxX - minX) / 2,
        centerY: minY + (maxY - minY) / 2,
      }
    } else if (this.isTripod()) {
      // TRIPOD의 경우 Mixin의 전용 경계 계산 메소드 사용
      const tripodMixin = this as unknown as {
        __getTransformerBounds: () => {
          top: number
          bottom: number
          left: number
          right: number
          centerX: number
          centerY: number
        }
      }
      return tripodMixin.__getTransformerBounds()
    }

    // 기본값: entity 자체의 위치
    return {
      left: this.node.x() - 20,
      right: this.node.x() + 20,
      top: this.node.y() - 20,
      bottom: this.node.y() + 20,
      centerX: this.node.x(),
      centerY: this.node.y(),
    }
  }

  /**
   * transformer 기반 메뉴 위치를 계산하는 함수
   */
  protected __calculateTransformerBasedPosition(isAbove: boolean): { x: number; y: number } {
    const transformerBounds = this.__getTransformerBounds()
    const stage = this.renderer.stage
    const scale = stage.scaleX()

    if (isAbove) {
      // 위쪽: transformer의 top(rotate handle 포함) 기준으로 위로 offset
      const offset = BUTTON_POSITION_CONFIG.TRANSFORMER_OFFSET_ABOVE / scale // zoom에 맞게 조정
      return {
        x: transformerBounds.centerX,
        y: transformerBounds.top - offset,
      }
    } else {
      // 아래쪽: transformer의 bottom 기준으로 아래로 offset
      const offset = BUTTON_POSITION_CONFIG.TRANSFORMER_OFFSET_BELOW / scale // zoom에 맞게 조정
      return {
        x: transformerBounds.centerX,
        y: transformerBounds.bottom + offset,
      }
    }
  }

  // 방향도형 랜더 업데이트
  protected __updateDirectionRender(): void {
    if (!this.__directionShape || !this.__directionPoints || this.__directionPoints.length < 4)
      return

    updateDirectionElements(
      this.renderer,
      this.__directionShape,
      this.__directionPoints,
      this.__color,
      this.calculatedConnectedLineWidth,
      this.calculatedArrowHeadSize,
      this.__directionStateManager.originalArrowHead,
      this.getDirectionBalloonClip(),
    )

    // 벌룬 위치 업데이트
    updateDirectionBalloonPosition(
      this.balloon,
      this.balloonDragDiff,
      this.__directionPoints,
      this.avaliableShape.x(),
      this.avaliableShape.y(),
      false, // 일반 모드
    )

    // 텍스트 위치 업데이트
    if (this.balloon) this.updateTextPosition()
  }

  /**
   * balloon 크기에 비례한 방향 재설정 임계값 계산
   */
  protected __getDirectionThreshold(): number {
    const balloonSize = this.calculatedBalloonSize
    // 기본 balloon 크기 60을 기준으로 비례 계산 (DIR_DRAW_DIRECTION_THRESHOLD = 30은 balloonSize 60 기준)
    const baseThreshold = 1.0
    // const baseBalloonSize = 60
    return balloonSize * baseThreshold
    // return (balloonSize / baseBalloonSize) * baseThreshold
  }

  // 방향도형 경로를 직접 업데이트 (시작점 근처에서 방향 재설정 지원)
  protected __updateDirectionPath(dragPos: { x: number; y: number }): void {
    if (!this.__directionShape || !this.__directionPoints || this.__directionPoints.length < 4)
      return

    // 시작점
    const startX = this.__directionPoints[0]
    const startY = this.__directionPoints[1]

    // balloon과 시작점 간의 거리 계산
    const distanceToStart = Math.sqrt(
      Math.pow(dragPos.x - startX, 2) + Math.pow(dragPos.y - startY, 2),
    )

    // 동적 임계값 계산 (balloon 크기에 비례)
    const dynamicThreshold = this.__getDirectionThreshold()

    // 방향 재설정 모드 체크 (시작점 근처로 드래그된 경우)
    const shouldEnterRedrawMode = distanceToStart <= dynamicThreshold

    if (shouldEnterRedrawMode && !this.__isDirectionRedrawMode) {
      // 방향 재설정 모드 진입
      this.__isDirectionRedrawMode = true
      this.__redrawStartDirection = null
    } else if (!shouldEnterRedrawMode && this.__isDirectionRedrawMode) {
      // 방향 재설정 모드 종료 (시작점에서 멀어짐)
      this.__isDirectionRedrawMode = false
      this.__redrawStartDirection = null
    }

    // 방향 재설정 모드에서의 처리
    if (this.__isDirectionRedrawMode) {
      // 새로운 방향 계산 (시작점에서 현재 드래그 위치로)
      const dx = dragPos.x - startX
      const dy = dragPos.y - startY

      // 최소 거리 체크 (너무 가까우면 방향 계산 불가)
      if (Math.abs(dx) < 5 && Math.abs(dy) < 5) {
        return // 너무 가까우면 방향 계산하지 않음
      }

      const newDirection = snapToDirections(dx, dy)

      // 방향이 변경되었을 때만 업데이트
      if (
        !this.__redrawStartDirection ||
        this.__redrawStartDirection.index !== newDirection.index
      ) {
        this.__redrawStartDirection = newDirection

        // 새로운 방향으로 전체 경로 재계산 (기존 꺾은점들 무시하고 직선으로)
        const distance = Math.max(distanceToStart, dynamicThreshold)
        const newEndX = startX + newDirection.x * distance
        const newEndY = startY + newDirection.y * distance

        // 단순한 직선 경로로 재설정
        this.__directionPoints = [startX, startY, newEndX, newEndY]

        // 상태 관리자 재초기화
        this.__directionStateManager.fixedDirection = newDirection
        this.__directionStateManager.fixedLines = [
          {
            start: { x: startX, y: startY },
            end: { x: newEndX, y: newEndY },
            direction: newDirection,
          },
        ]
        this.__directionStateManager.activeLineIndex = 0
        this.__directionStateManager.isDirectionFixed = true

        // 화살표 머리 방향도 새로운 방향으로 업데이트
        this.__directionStateManager.originalArrowHead = {
          startX: startX,
          startY: startY,
          direction: { x: newDirection.x, y: newDirection.y },
        }

        // 렌더
        this.__updateDirectionRender()
      }
      return
    }

    // 기존 방향 유지 모드 (일반적인 경로 업데이트)
    // 상태 초기화
    if (!this.__directionStateManager.isDirectionFixed) {
      initializeDirectionDragState(this.__directionStateManager, this.__directionPoints)
    }

    if (!this.__directionStateManager.fixedDirection) return

    const scale = this.node.scaleX()

    // 경로 계산
    const result = calculateDirectionPath(
      startX,
      startY,
      dragPos.x,
      dragPos.y,
      this.__directionStateManager.fixedDirection,
      this.__directionStateManager.fixedLines,
      this.__directionStateManager.activeLineIndex,
      scale,
    )

    // 첫 번째 선분 길이 확인 - 임계값 이하로 줄어들면 최소 길이로 보정
    if (result.path.length >= 2) {
      const firstLineLength = Math.sqrt(
        Math.pow(result.path[1].x - result.path[0].x, 2) +
          Math.pow(result.path[1].y - result.path[0].y, 2),
      )

      if (firstLineLength < dynamicThreshold) {
        // 수정시에는 방향성을 안바꿀것임으로 tempArrow 모드로 전환하지 않고 최소 길이 보장
        const direction = this.__directionStateManager.fixedDirection
        const minEndX = startX + direction.x * dynamicThreshold
        const minEndY = startY + direction.y * dynamicThreshold

        // 최소 길이로 경로 재계산
        const minResult = calculateDirectionPath(
          startX,
          startY,
          minEndX,
          minEndY,
          direction,
          [],
          0,
          scale,
        )

        // 상태 업데이트
        this.__directionStateManager.fixedLines = minResult.newFixedLines
        this.__directionStateManager.activeLineIndex = minResult.newActiveIndex

        // 경로 업데이트
        this.__directionPoints = []
        minResult.path.forEach((point: Vector2d) => {
          this.__directionPoints.push(point.x, point.y)
        })

        // 렌더
        this.__updateDirectionRender()
        return
      }
    }

    this.__directionStateManager.fixedLines = result.newFixedLines
    this.__directionStateManager.activeLineIndex = result.newActiveIndex

    // 경로 업데이트
    this.__directionPoints = []
    result.path.forEach((point: Vector2d) => {
      this.__directionPoints.push(point.x, point.y)
    })

    // 렌더
    this.__updateDirectionRender()
  }

  public applyBalloonScale(_scale: number): void {
    if (this.balloon) {
      const balloonSize = this.calculatedBalloonSize
      const balloonThickness = this.calculatedBalloonThickness

      this.balloon.head.rect.width(balloonSize)
      this.balloon.head.rect.height(balloonSize)
      this.balloon.head.rect.offsetX(balloonSize / 2)
      this.balloon.head.rect.offsetY(balloonSize / 2)
      this.balloon.head.rect.strokeWidth(balloonThickness)

      this.updateBalloonPosition()
      this.__refreshDirectionBalloonClip() // 벌룬 크기에 맞춰 방향도형 선분 클립 갱신
    }
  }

  /**
   * 현재 렌더된 선 굵기를 유지한 채 방향도형의 벌룬 겹침 클립만 갱신합니다.
   * (전역 벌룬 크기 변경 시 선 굵기의 개별/전역 출처를 바꾸지 않기 위함)
   */
  protected __refreshDirectionBalloonClip(): void {
    if (this.entityType !== SHAPES.DIRECTION || !this.__directionShape) return

    const lines = this.__directionShape.find('Line') as ILine[]
    const currentStrokeWidth =
      lines.length > 0 ? lines[0].strokeWidth() : this.calculatedConnectedLineWidth

    updateDirectionElements(
      this.renderer,
      this.__directionShape,
      this.__directionPoints,
      this.__color,
      currentStrokeWidth,
      this.calculatedArrowHeadSize,
      this.__directionStateManager.originalArrowHead,
      this.getDirectionBalloonClip(),
    )
  }

  public applyAreaLineWidthDirection(): void {
    // 방향도형인 경우 선 굵기 업데이트 (화살표 크기는 변경하지 않음)
    if (this.entityType === SHAPES.DIRECTION && this.__directionShape) {
      updateDirectionElements(
        this.renderer,
        this.__directionShape,
        this.__directionPoints,
        this.__color,
        1 * this.canvasEngine.connectedLineWidth,
        this.calculatedArrowHeadSize, // 화살표 크기는 개별 설정된 크기 유지
        this.__directionStateManager.originalArrowHead,
        this.getDirectionBalloonClip(),
      )
    }
  }

  public transformerDragable(value: boolean): void {
    if (this.__shapeTransformer) {
      this.__shapeTransformer.draggable(value)
    }
    if (this.__tripod_transform_group) {
      this.__tripod_transform_group.draggable(value)
    }
    if (this.__shape) {
      this.__shape.draggable(value)
    }
    if (this.__brush) {
      this.__brush.draggable(value)
    }
  }
}
